// Component: snippets/go-batch-urgency.liquid (loaded from the snippet).
/**
 * <go-batch-urgency>: three pre-order batch rows for out-of-stock variants.
 * Markup: snippets/go-batch-urgency.liquid. All dates are Amsterdam calendar days handled as
 * UTC-noon timestamps, so day arithmetic is immune to DST.
 *
 * Fixed calendar: Mondays every cycle (1, 2 or 4 weeks) from the calendar start.
 * Batch 2: first calendar Monday on or after (this Monday + max weeks from the variant delivery metafield).
 * Batch 1: batch 2 - cycle, sold out. Batch 3: batch 2 + cycle, available.
 * When batch 2 closes it becomes batch 1 and batch 3 becomes batch 2.
 * Percentage (batch 2): rises evenly from start to max over the days the batch is batch 2.
 * Each row is shown as a Monday-Friday range. While visible, the delivery row is hidden with CSS.
 */
const DAY = 86400000;
// Week words in every market language; day-based messages ("werkdagen", "Werktage") never match.
const WEEK_PATTERN = /week|weken|woche|semaine|settiman|semana|tydzie|tygodn|týden|týdn|týžd|hét|veck|viikk|\buge|εβδομάδ|săptămân|tjed|nedēļ|savait/i;

class GoBatchUrgency extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    try {
      this.messages = JSON.parse(this.querySelector('script[data-messages]').textContent) || {};
    } catch (error) {
      this.messages = {};
    }
    this.variantId = this.dataset.variantId;

    this.onVariantChange = (event) => {
      const detail = event.detail || {};
      // detail.formId is the form's .id property, which the form's <input name="id"> shadows; read the attribute.
      if (event.target.getAttribute?.('id') !== this.dataset.formId || !detail.variant) return;
      this.variantId = String(detail.variant.id);
      this.render();
    };
    document.addEventListener('variant:change', this.onVariantChange);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  // Max number in a weeks message, e.g. "2-3 weken" -> 3, "da 2 a 3 settimane" -> 3. Null otherwise.
  static parseWeeks(text) {
    if (!text || !WEEK_PATTERN.test(text)) return null;
    const numbers = (text.match(/\d+/g) || []).map(Number);
    return numbers.length ? Math.max(...numbers) : null;
  }

  static parseDay(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    return match ? Date.UTC(+match[1], +match[2] - 1, +match[3], 12) : null;
  }

  // Today in Amsterdam as a UTC-noon timestamp. ?batch_today=YYYY-MM-DD overrides it for demos.
  static today() {
    const override = GoBatchUrgency.parseDay(new URLSearchParams(location.search).get('batch_today'));
    if (override) return override;
    const parts = {};
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date())
      .forEach((part) => (parts[part.type] = part.value));
    return Date.UTC(+parts.year, +parts.month - 1, +parts.day, 12);
  }

  static weekday(ts) {
    return new Date(ts).getUTCDay();
  }

  compute(weeks) {
    const today = GoBatchUrgency.today();
    const mondayOf = (ts) => ts - ((GoBatchUrgency.weekday(ts) + 6) % 7) * DAY;
    const monday = mondayOf(today);
    const parsedStart = GoBatchUrgency.parseDay(this.dataset.calendarStart);
    if (!parsedStart) return null;
    const calendarStart = mondayOf(parsedStart); // tolerate a non-Monday setting
    const cycleWeeks = [1, 2, 4].includes(+this.dataset.cycleWeeks) ? +this.dataset.cycleWeeks : 2;
    const cycle = cycleWeeks * 7 * DAY;
    const startPercent = parseInt(this.dataset.startPercent, 10) || 80;
    const maxPercent = parseInt(this.dataset.maxPercent, 10) || 93;

    // Batch 2: first calendar Monday on or after the delivery week.
    const target = monday + weeks * 7 * DAY;
    const current = calendarStart + Math.ceil((target - calendarStart) / cycle) * cycle;
    const past = current - cycle;
    const future = current + cycle;

    // First Monday on which this batch became batch 2; % rises evenly to maxPercent on the cycle's last day.
    const opened = current - cycle - weeks * 7 * DAY + 7 * DAY;
    const daysOpen = Math.round((today - opened) / DAY);
    const cycleDays = cycleWeeks * 7;
    const percent = Math.min(maxPercent, startPercent + Math.round((daysOpen * (maxPercent - startPercent)) / (cycleDays - 1)));
    return { past, current, future, percent };
  }

  // Monday-Friday range in the visitor's locale. Intl puts an en dash between dates; the design rules
  // forbid en dashes in UI copy, so it is swapped for a plain hyphen (Intl pads it with thin spaces when the
  // month changes, those become normal spaces).
  formatRange(monday) {
    const format = new Intl.DateTimeFormat(this.dataset.locale || 'nl', { day: 'numeric', month: 'long', timeZone: 'UTC' });
    return format.formatRange(new Date(monday), new Date(monday + 4 * DAY)).replace(/(\s*)[–—](\s*)/g, (match, before, after) => (before || after ? ' - ' : '-'));
  }

  fillRow(name, ts, percent, status) {
    const row = this.querySelector(`[data-batch="${name}"]`);
    row.querySelector('[data-date]').textContent = this.formatRange(ts);
    row.style.setProperty('--go-batch-urgency-fill', `${percent}%`);
    if (status !== null) row.querySelector('[data-status]').textContent = status;
  }

  render() {
    const weeks = GoBatchUrgency.parseWeeks(this.messages[this.variantId]);
    const result = weeks ? this.compute(weeks) : null;
    if (!result) {
      // Theme editor only: keep the block visible with a note instead of vanishing.
      const showNote = this.hasAttribute('data-design-mode');
      this.hidden = !showNote;
      this.setEditorNote(showNote);
      this.toggleDeliveryRow(false);
      return;
    }
    this.setEditorNote(false);
    const template = this.dataset.percentTemplate || '[[P]]%';
    this.fillRow('past', result.past, 100, null);
    this.fillRow('current', result.current, result.percent, template.replace('[[P]]', result.percent));
    this.fillRow('future', result.future, 0, this.dataset.availableText || '');
    this.hidden = false;
    this.toggleDeliveryRow(true);
  }

  setEditorNote(show) {
    const note = this.querySelector('[data-editor-note]');
    if (!note) return;
    note.hidden = !show;
    this.querySelector('.go-batch-urgency__title').hidden = show;
    this.querySelector('.go-batch-urgency__list').hidden = show;
  }

  // Hide this product's own delivery row (kept in the DOM) while the widget rows are visible.
  toggleDeliveryRow(active) {
    const deliveryRow = this.closest('.product-info')?.querySelector('.et-delivery-stock');
    if (deliveryRow) deliveryRow.toggleAttribute('data-go-batch-hidden', active);
  }
}

if (!customElements.get('go-batch-urgency')) customElements.define('go-batch-urgency', GoBatchUrgency);
